import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, ShoppingCart, Clock, CheckCircle, XCircle, AlertCircle, User, Building2, MapPin, Calendar, Phone, Mail, FileText, Eye, Upload, Download, ExternalLink, TrendingDown, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
// Removed mockData imports - using real API data
import { apiService } from '../services/api';
import { Order, OrderItem, Location, Company, OrderUser, Supplier, Capex } from '../types/order';
import RoleBasedComponent from './RoleBasedComponent';
import { useAuth } from '../contexts/AuthContext';
import { useMenuPermission } from '../hooks/useMenuPermission';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const OrdersPage: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const { canCreate, canUpdate, canDelete } = useMenuPermission('Siparişler');
  const { t, language } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('orders', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderActivities, setOrderActivities] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());

  const [ordersData, setOrdersData] = useState<Order[]>([]);
  const [allOrdersData, setAllOrdersData] = useState<Order[]>([]); // Tüm siparişler için
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [suppliersData, setSuppliersData] = useState<any[]>([]);
  const [capexData, setCapexData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<OrderUser[]>([]);
  const [companyCapexData, setCompanyCapexData] = useState<any[]>([]);
  const [selectedCapex, setSelectedCapex] = useState<any>(null);
  const [exchangeRates, setExchangeRates] = useState<any>({ TRY: 1, USD: 34.5, EUR: 37.2, lastUpdated: null });
  const [loading, setLoading] = useState(true);

  // Item suggestion states
  const [itemSuggestions, setItemSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<{[key: number]: boolean}>({});
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  // Price search states
  const [priceSearchLoading, setPriceSearchLoading] = useState<{[key: number]: boolean}>({});
  const [priceSearchResults, setPriceSearchResults] = useState<{[key: number]: any}>({});
  const [showPricePopup, setShowPricePopup] = useState<{[key: number]: boolean}>({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const loadCompanyCapex = async (companyId: number) => {
    if (!companyId) {
      setCompanyCapexData([]);
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/capex/by-company/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanyCapexData(Array.isArray(data) ? data : []);
      } else {
        setCompanyCapexData([]);
      }
    } catch (error) {
      setCompanyCapexData([]);
    }
  };
  
  interface OrderDataState {
    order_number: string;
    beam_order_number: string;
    ticket_number: string;
    location_id: number;
    company_id: number;
    capex_id: number;
    user_id: number;
    supplier_id: number;
    category: Order['category'];
    total_amount: number;
    currency: 'TRY' | 'USD' | 'EUR';
    status: Order['status'];
    description: string;
    notes: string;
    delivery_date: string;
    invoice_url: string;
    items: OrderItem[];
    use_capex: boolean;
  }

  const [orderData, setOrderData] = useState<OrderDataState>({
    order_number: '',
    beam_order_number: '',
    ticket_number: '',
    location_id: 0,
    company_id: 0,
    capex_id: 0,
    user_id: 0,
    supplier_id: 0,
    category: 'bilgisayar' as Order['category'],
    total_amount: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    status: 'siparis-olusturuldu' as Order['status'],
    description: '',
    notes: '',
    delivery_date: '',
    invoice_url: '',
    items: [{ item_name: '', quantity: 1, unit_price: 0, vat_rate: 20 }],
    use_capex: false
  });

  // Load exchange rates from TCMB
  const loadExchangeRates = async () => {
    try {
      const rates = await apiService.getExchangeRates();
      
      if (rates.USD && rates.EUR) {
        setExchangeRates({
          ...rates,
          lastUpdated: rates.lastUpdated || new Date().toISOString()
        });
      } else {
        throw new Error('Geçersiz kur verisi');
      }
    } catch (error: any) {
      setExchangeRates(prev => ({
        ...prev,
        error: error.message || 'Kurlar yüklenirken hata oluştu'
      }));
    }
  };

  // Update exchange rates from TCMB
  const updateExchangeRates = async () => {
    try {
      const result = await apiService.updateExchangeRates();
      
      if (result.success) {
        setExchangeRates({
          ...result.rates,
          lastUpdated: result.rates.lastUpdated || new Date().toISOString()
        });
        
        showGlobalNotification.success(
          t('order.notifExchangeUpdated'),
          `${result.message} - ${result.updatedBy} tarafından`,
          { duration: 5000 }
        );
      } else {
        showGlobalNotification.error(
          t('order.notifExchangeError'),
          result.message || t('order.notifExchangeError'),
          { duration: 5000 }
        );
      }
    } catch (error: any) {
      showGlobalNotification.error(
        t('order.notifExchangeError'),
        error.message || t('order.notifExchangeError'),
        { duration: 5000 }
      );
    }
  };

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Tüm siparişleri ayrı olarak yükle (kartlar için)
  React.useEffect(() => {
    loadAllOrders();
  }, []);

  React.useEffect(() => {
    setCurrentPage(1);
    if (!searchTerm) {
      loadData(1);
    }
  }, [filterStatus]);

  // Yıl filtresi değiştiğinde veri yükle
  React.useEffect(() => {
    setCurrentPage(1);
    loadData(1);
    loadAllOrders();
  }, [filterYear]);

  // Arama terimi değiştiğinde sayfayı 1'e resetle
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Bildirimden belirli bir sipariş açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'orders' || !e.detail?.entityId) return;
      try {
        const order = await apiService.getOrderById(e.detail.entityId);
        if (order) showOrderDetail(order);
      } catch {}
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  // Tüm siparişleri yükle (kartlar için)
  const loadAllOrders = async () => {
    try {
      // Direkt fetch kullanarak tüm siparişleri yükle
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/orders?limit=1000&year=${filterYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.orders && Array.isArray(data.orders)) {
          setAllOrdersData(data.orders);
        } else {
          setAllOrdersData([]);
        }
      } else {
        setAllOrdersData([]);
      }
    } catch (error) {
      setAllOrdersData([]);
    }
  };

  const loadData = async (page: number = 1) => {
    try {
      setLoading(true);
      const [ordersResponse, companiesResponse, locationsResponse, suppliersResponse, capexResponse, usersResponse] = await Promise.all([
        apiService.getOrders({
          page,
          limit: 10,
          ...(filterStatus !== 'all' && { status: filterStatus }),
          year: filterYear
        }),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getSuppliers().catch(() => []),
        apiService.getCapex().catch(() => []),
        apiService.getUsers().catch(() => [])
      ]);
      
      // Load current exchange rates
      await loadExchangeRates();
      
      // API response format kontrolü - güvenli array kontrolü
      const safeArrayResponse = (response: any, responseName: string) => {
        if (Array.isArray(response)) {
          return response;
        } else if (response && Array.isArray(response.data)) {
          return response.data;
        } else if (response && response[responseName] && Array.isArray(response[responseName])) {
          return response[responseName];
        } else {
          return [];
        }
      };

      // Handle pagination for orders
      if (ordersResponse && ordersResponse.orders) {
        setOrdersData(ordersResponse.orders);
        setPagination(ordersResponse.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 10,
          hasNextPage: false,
          hasPrevPage: false
        });
        setCurrentPage(ordersResponse.pagination?.currentPage || 1);
        setTotalPages(ordersResponse.pagination?.totalPages || 1);
        setTotalCount(ordersResponse.pagination?.totalCount || 0);
      } else {
        setOrdersData(safeArrayResponse(ordersResponse, 'orders'));
      }

      setCompaniesData(safeArrayResponse(companiesResponse, 'companies'));
      setLocationsData(safeArrayResponse(locationsResponse, 'locations'));
      setSuppliersData(safeArrayResponse(suppliersResponse, 'suppliers'));
      setCapexData(safeArrayResponse(capexResponse, 'capex'));
      setUsersData(safeArrayResponse(usersResponse, 'users'));
    } catch (error) {
      showGlobalNotification.error(t('order.notifError'), t('order.notifLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    'bilgisayar',
    'telefon',
    'ekipman',
    'sarf-malzeme',
    'elektronik',
    'switch',
    'server',
    'bakim',
    'kamera-sistemi',
    'ses-sistemi',
    'monitor',
    'el-terminali',
    'telsiz'
  ];

  const getCategoryLabel = (cat: string): string => {
    const map: Record<string, string> = {
      'bilgisayar': t('order.catComputer'),
      'telefon': t('order.catPhone'),
      'ekipman': t('order.catEquipment'),
      'sarf-malzeme': t('order.catConsumables'),
      'elektronik': t('order.catElectronics'),
      'switch': t('order.catSwitch'),
      'server': t('order.catServer'),
      'bakim': t('order.catMaintenance'),
      'kamera-sistemi': t('order.catCamera'),
      'ses-sistemi': t('order.catSound'),
      'monitor': t('order.catMonitor'),
      'el-terminali': t('order.catHandheld'),
      'telsiz': t('order.catRadio'),
    };
    return map[cat] || cat;
  };

  const statusOptions = [
    { value: 'siparis-olusturuldu', label: t('order.statusCreated'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'beklemede', label: t('order.statusPending'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'siparis-verildi', label: t('order.statusOrdered'), color: 'bg-indigo-100 text-indigo-800' },
    { value: 'teslim-edildi', label: t('order.statusDelivered'), color: 'bg-green-100 text-green-800' },
    { value: 'fatura-yuklendi', label: t('order.statusInvoiceUploaded'), color: 'bg-primary/20 text-primary-dark' },
    { value: 'fatura-onaylandi', label: t('order.statusInvoiceApproved'), color: 'bg-emerald-100 text-emerald-800' },
    { value: 'tamamlandi', label: t('order.statusCompleted'), color: 'bg-purple-100 text-purple-800' },
    { value: 'iptal-edildi', label: t('order.statusCancelled'), color: 'bg-red-100 text-red-800' }
  ];

  const priorityOptions = [
    { value: 'dusuk', label: t('order.priorityLow'), color: 'text-gray-600 dark:text-gray-400' },
    { value: 'normal', label: t('order.priorityNormal'), color: 'text-primary' },
    { value: 'yuksek', label: t('order.priorityHigh'), color: 'text-orange-600' },
    { value: 'acil', label: t('order.priorityUrgent'), color: 'text-red-600' }
  ];

  // Filtreleme yapıldığında tüm siparişler üzerinden filtreleme yap
  const dataToFilter = (searchTerm || filterStatus !== 'all') ? allOrdersData : ordersData;
  
  const filteredOrders = dataToFilter.filter(order => {
    const matchesSearch = !searchTerm || (
      String(order.order_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(order.ticket_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(order.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(order.company?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(order.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.beam_order_number && String(order.beam_order_number).toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.items && order.items.some(item => String(item.item_name || '').toLowerCase().includes(searchTerm.toLowerCase())))
    );
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'completed' && (order.status === 'fatura-yuklendi' || order.status === 'fatura-onaylandi' || order.status === 'tamamlandi')) ||
      (filterStatus === 'my-orders' && user && order.user_id === user.id) ||
      order.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Filtreleme yapıldığında frontend pagination kullan
  const itemsPerPage = 10;
  const useFrontendPagination = (searchTerm || filterStatus !== 'all');
  const totalFilteredPages = useFrontendPagination ? Math.ceil(filteredOrders.length / itemsPerPage) : totalPages;
  const currentPageToUse = useFrontendPagination ? currentPage : pagination.currentPage;
  
  const paginatedFilteredOrders = useFrontendPagination
    ? filteredOrders.slice((currentPageToUse - 1) * itemsPerPage, currentPageToUse * itemsPerPage)
    : filteredOrders;

  const handleEdit = (order: Order) => {
    // Fatura onaylanan siparişler düzenlenemez
    if (order.status === 'fatura-onaylandi') {
      Swal.fire({
        title: t('order.swalEditBlockedTitle'),
        text: t('order.swalEditBlockedText'),
        icon: 'warning',
        confirmButtonText: t('order.swalOk'),
        confirmButtonColor: '#3b82f6'
      });
      return;
    }
    
    setEditingOrder(order);
    const hasCapex = order.capex_id && order.capex_id > 0;
    
    setOrderData({
      order_number: order.order_number,
      beam_order_number: order.beam_order_number || '',
      ticket_number: order.ticket_number,
      location_id: order.location_id,
      company_id: order.company_id,
      capex_id: order.capex_id || 0,
      user_id: order.user_id,
      supplier_id: order.supplier_id || 0,
      category: order.category,
      total_amount: order.total_amount,
      currency: order.currency,
      status: order.status,
      description: order.description,
      notes: order.notes || '',
      delivery_date: order.delivery_date ? order.delivery_date.split('T')[0] : '',
      invoice_url: order.invoice_url || '',
      items: order.items || [{ item_name: '', quantity: 1, unit_price: 0, vat_rate: 20 }],
      use_capex: hasCapex
    });
    
    // Load company CAPEX if order has CAPEX
    if (hasCapex && order.company_id) {
      loadCompanyCapex(order.company_id);
      // Set selected CAPEX
      const capex = capexData.find(c => c.id === order.capex_id);
      setSelectedCapex(capex || null);
    }
    
    setShowModal(true);
  };

  const generateNextOrderNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `SP-${year}-${month}-`;

    // Get existing order numbers for current month from ALL orders (not just current page)
    const existingNumbers = allOrdersData
      .filter(order => order.order_number.startsWith(prefix))
      .map(order => {
        const parts = order.order_number.split('-');
        return parseInt(parts[3]) || 0;
      })
      .sort((a, b) => b - a); // Sort descending to get highest number first

    // Get the highest number and add 1
    const highestNumber = existingNumbers.length > 0 ? existingNumbers[0] : 0;
    const nextNumber = highestNumber + 1;

    return `${prefix}${String(nextNumber).padStart(3, '0')}`;
  };

  // Fetch item suggestions with debounce
  const fetchItemSuggestions = async (search: string, itemIndex: number) => {
    if (!search || search.length < 4) {
      setItemSuggestions([]);
      setShowSuggestions({...showSuggestions, [itemIndex]: false});
      return;
    }

    setSuggestionLoading(true);
    try {
      const suggestions = await apiService.getItemSuggestions(search);
      setItemSuggestions(suggestions);
      setShowSuggestions({...showSuggestions, [itemIndex]: true});
    } catch (error) {
      setItemSuggestions([]);
    } finally {
      setSuggestionLoading(false);
    }
  };

  // Debounce timer ref
  const suggestionTimerRef = React.useRef<any>(null);

  const handleItemNameChange = (index: number, value: string) => {
    const newItems = [...(orderData.items || [])];
    newItems[index] = {...newItems[index], item_name: value};
    setOrderData({...orderData, items: newItems});

    // Clear previous timer
    if (suggestionTimerRef.current) {
      clearTimeout(suggestionTimerRef.current);
    }

    // Set new timer for debounced search
    suggestionTimerRef.current = setTimeout(() => {
      fetchItemSuggestions(value, index);
    }, 300);
  };

  const selectSuggestion = (index: number, suggestion: any) => {
    const newItems = [...(orderData.items || [])];
    newItems[index] = {
      ...newItems[index],
      item_name: suggestion.item_name,
      unit_price: suggestion.unit_price,
      vat_rate: suggestion.vat_rate
    };
    // Para birimini de önerilen ürünün para birimine güncelle
    setOrderData({
      ...orderData,
      items: newItems,
      currency: suggestion.currency as 'TRY' | 'USD' | 'EUR'
    });
    setShowSuggestions({...showSuggestions, [index]: false});
    setItemSuggestions([]);
  };

  // Price search function
  const searchPrice = async (index: number, itemName: string) => {
    if (!itemName || itemName.trim().length < 3) {
      showGlobalNotification.warning(t('order.notifWarning'), t('order.notifMinSearch'));
      return;
    }

    setPriceSearchLoading({...priceSearchLoading, [index]: true});
    setShowPricePopup({...showPricePopup, [index]: false});

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/price-search/search?query=${encodeURIComponent(itemName.trim())}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      setPriceSearchResults({...priceSearchResults, [index]: data});
      setShowPricePopup({...showPricePopup, [index]: true});

    } catch (error) {
      console.error('Price search error:', error);
      showGlobalNotification.error(t('order.notifError'), t('order.notifPriceSearchError'));
    } finally {
      setPriceSearchLoading({...priceSearchLoading, [index]: false});
    }
  };

  const handleAdd = () => {
    setEditingOrder(null);
    const nextOrderNumber = generateNextOrderNumber();
    
    // Generate unique ticket number
    const generateUniqueTicketNumber = () => {
      const existingTickets = allOrdersData
        .map(order => order.ticket_number)
        .filter(ticket => ticket && ticket.startsWith('TK-'));

      // Extract numbers and find the highest
      const existingNumbers = existingTickets
        .map(ticket => {
          const parts = ticket.split('-');
          return parseInt(parts[1]) || 0;
        })
        .sort((a, b) => b - a);

      // Get the highest number and add 1
      const highestNumber = existingNumbers.length > 0 ? existingNumbers[0] : 0;
      const nextNumber = highestNumber + 1;

      return `TK-${String(nextNumber).padStart(6, '0')}`;
    };
    
    const nextTicketNumber = generateUniqueTicketNumber();
    const deliveryDate = new Date();
    deliveryDate.setMonth(deliveryDate.getMonth() + 1);
    
    setOrderData({
      order_number: nextOrderNumber,
      ticket_number: nextTicketNumber,
      location_id: user?.location_id || 0,
      company_id: 0,
      capex_id: 0,
      user_id: user?.id || 0,
      supplier_id: 0,
      category: 'bilgisayar',
      total_amount: 0,
      currency: 'TRY',
      status: 'siparis-olusturuldu',
      description: '',
      notes: '',
      delivery_date: deliveryDate.toISOString().split('T')[0],
      invoice_url: '',
      items: [{ item_name: '', quantity: 1, unit_price: 0, vat_rate: 20 }]
    });
    setShowModal(true);
  };

  // Shared invoice approval dialog — used both after upload and from the "Faturayı Onayla" button
  const showInvoiceApprovalDialog = async (order: Order): Promise<{ invoiceAmount: number; invoiceCurrency: string } | null> => {
    const originalAmount = order.total_amount;
    const originalCurrency = order.currency;
    const hasCapex = !!(order.capex_id && order.capex_id > 0);

    let capexInfo: any = null;
    if (hasCapex) {
      capexInfo = await apiService.getOrderCapexInfo(order.id);
    }

    const capexUsageEUR: number | null = capexInfo?.capex_usage_eur ?? null;
    const eurRateNow: number = capexInfo?.eur_rate_now ?? (exchangeRates.EUR || 37.2);
    const usdRateNow: number = capexInfo?.usd_rate_now ?? (exchangeRates.USD || 34.5);

    const toEUR = (amount: number, currency: string): number => {
      if (currency === 'EUR') return amount;
      if (currency === 'USD') return amount * (usdRateNow / eurRateNow);
      if (currency === 'TRY') return amount / eurRateNow;
      return amount;
    };

    const capexHtml = hasCapex && capexUsageEUR != null ? `
      <div style="margin-bottom:10px; padding:10px; background:#f5f3ff; border:1px solid #e9d5ff; border-radius:6px; font-size:13px;">
        <div style="font-weight:600; color:#7c3aed; margin-bottom:6px;">${t('order.swalCapexInfoTitle')} ${capexInfo?.capex_number || ''}</div>
        <div style="display:flex; justify-content:space-between; margin-bottom:3px;">
          <span style="color:#6b7280;">${t('order.swalCapexOrderAmount')}</span>
          <span style="font-weight:600; color:#374151;">${originalCurrency} ${parseFloat(String(originalAmount)).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
        </div>
        <div style="display:flex; justify-content:space-between; margin-bottom:3px;">
          <span style="color:#6b7280;">${t('order.swalCapexDeducted')}</span>
          <span style="font-weight:700; color:#7c3aed;">€${capexUsageEUR.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
        </div>
        <div style="display:flex; justify-content:space-between;">
          <span style="color:#6b7280;">${t('order.swalCapexCurrentRate')}</span>
          <span style="color:#374151;">1 EUR = ₺${eurRateNow.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 4 })}</span>
        </div>
      </div>
    ` : hasCapex ? `
      <div style="margin-bottom:10px; padding:8px 10px; background:#fef3c7; border:1px solid #fcd34d; border-radius:6px; font-size:12px; color:#92400e;">
        ${t('order.swalCapexWarning')}
      </div>
    ` : '';

    const result = await Swal.fire({
      title: t('order.swalInvoiceTitle'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: t('order.swalApprove'),
      cancelButtonText: t('order.swalCancel'),
      reverseButtons: true,
      width: 520,
      html: `
        <div style="text-align:left; font-size:14px;">
          <p style="margin-bottom:12px; color:#374151;">
            <strong>${order.order_number}</strong> ${t('order.swalInvoiceText')}
          </p>
          ${capexHtml}
          <div style="display:flex; gap:8px; align-items:flex-end; margin-bottom:8px;">
            <div style="flex:1;">
              <label style="display:block; font-size:12px; font-weight:600; color:#374151; margin-bottom:4px;">${t('order.swalInvoiceAmountLabel')}</label>
              <input id="swal-invoice-amount" type="number" step="0.01" min="0.01"
                value="${parseFloat(String(originalAmount)).toFixed(2)}"
                style="width:100%; padding:8px 10px; border:1px solid #d1d5db; border-radius:6px; font-size:14px; outline:none; box-sizing:border-box;"
                onfocus="this.style.borderColor='#10b981'" onblur="this.style.borderColor='#d1d5db'"
              />
            </div>
            <div style="width:90px;">
              <label style="display:block; font-size:12px; font-weight:600; color:#374151; margin-bottom:4px;">${t('order.swalInvoiceCurrencyLabel')}</label>
              <select id="swal-invoice-currency"
                style="width:100%; padding:8px 6px; border:1px solid #d1d5db; border-radius:6px; font-size:14px; background:#fff; box-sizing:border-box;"
              >
                <option value="TRY" ${originalCurrency === 'TRY' ? 'selected' : ''}>TRY</option>
                <option value="USD" ${originalCurrency === 'USD' ? 'selected' : ''}>USD</option>
                <option value="EUR" ${originalCurrency === 'EUR' ? 'selected' : ''}>EUR</option>
              </select>
            </div>
          </div>
          ${hasCapex && capexUsageEUR != null ? `
          <div id="swal-capex-calc" style="padding:10px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:6px; font-size:13px; margin-bottom:8px;">
            <div style="display:flex; justify-content:space-between; margin-bottom:4px;">
              <span style="color:#6b7280;">${t('order.swalInvoiceEurLabel')}</span>
              <span id="swal-invoice-eur" style="font-weight:700; color:#059669;">€${toEUR(parseFloat(String(originalAmount)), originalCurrency).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div style="display:flex; justify-content:space-between; border-top:1px solid #bbf7d0; padding-top:4px; margin-top:4px;">
              <span style="color:#7c3aed; font-weight:600;">${t('order.swalCapexRefundLabel')}</span>
              <span id="swal-refund-eur" style="font-weight:700; color:#7c3aed;">€${Math.max(0, capexUsageEUR - toEUR(parseFloat(String(originalAmount)), originalCurrency)).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          ` : ''}
          <p style="font-size:11px; color:#9ca3af;">
            ${t('order.swalInvoiceFootnote')}${hasCapex ? t('order.swalInvoiceFootnoteCapex') : ''}
          </p>
        </div>
      `,
      didOpen: () => {
        if (!hasCapex || capexUsageEUR == null) return;
        const amountEl = document.getElementById('swal-invoice-amount') as HTMLInputElement;
        const currencyEl = document.getElementById('swal-invoice-currency') as HTMLSelectElement;
        const invoiceEurEl = document.getElementById('swal-invoice-eur');
        const refundEl = document.getElementById('swal-refund-eur');
        if (!amountEl || !currencyEl || !invoiceEurEl || !refundEl) return;

        const recalc = () => {
          const amt = parseFloat(amountEl.value);
          if (isNaN(amt) || amt <= 0) { invoiceEurEl.textContent = '—'; refundEl.textContent = '—'; return; }
          const cur = currencyEl.value;
          let eur = amt;
          if (cur === 'TRY') eur = amt / eurRateNow;
          else if (cur === 'USD') eur = amt * (usdRateNow / eurRateNow);
          const refund = Math.max(0, capexUsageEUR - eur);
          invoiceEurEl.textContent = `€${eur.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
          refundEl.textContent = `€${refund.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
          refundEl.style.color = refund > 0.01 ? '#7c3aed' : '#6b7280';
        };
        amountEl.addEventListener('input', recalc);
        currencyEl.addEventListener('change', recalc);
      },
      preConfirm: () => {
        const amountInput = document.getElementById('swal-invoice-amount') as HTMLInputElement;
        const currencyInput = document.getElementById('swal-invoice-currency') as HTMLSelectElement;
        const amount = parseFloat(amountInput.value);
        if (isNaN(amount) || amount <= 0) {
          Swal.showValidationMessage(t('order.swalValidAmount'));
          return false;
        }
        return { amount, currency: currencyInput.value };
      }
    });

    if (result.isConfirmed && result.value) {
      return { invoiceAmount: result.value.amount, invoiceCurrency: result.value.currency };
    }
    return null;
  };

  const handleInvoiceUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && editingOrder) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showGlobalNotification.error(t('order.notifError'), t('order.notifFileTypeError'));
        return;
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        showGlobalNotification.error(t('order.notifError'), t('order.notifFileSizeError'));
        return;
      }

      try {
        // Upload invoice via API with FormData
        const result = await apiService.uploadOrderInvoice(editingOrder.id, file);

        // Log activity
        await logActivity('upload_invoice', `${editingOrder.order_number} siparişine fatura yüklendi (${file.name})`, editingOrder.id.toString());

        // Update local state to show uploaded invoice first
        const orderWithInvoice = {...editingOrder, invoice_url: result.invoice_url, status: 'fatura-yuklendi' as Order['status']};
        setEditingOrder(orderWithInvoice);
        setOrderData({...orderData, status: 'fatura-yuklendi', invoice_url: result.invoice_url});
        setOrdersData(prevOrders =>
          prevOrders.map(o => o.id === editingOrder.id ? {...o, invoice_url: result.invoice_url, status: 'fatura-yuklendi'} : o)
        );

        // Ask if they want to approve now — show invoice amount dialog
        const approved = await Swal.fire({
          title: t('order.swalInvoiceUploadedTitle'),
          text: t('order.swalInvoiceUploadedText'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: t('order.swalInvoiceApproveYes'),
          cancelButtonText: t('order.swalInvoiceApproveNo'),
          confirmButtonColor: '#10b981',
          cancelButtonColor: '#6b7280',
          reverseButtons: true
        });

        if (approved.isConfirmed) {
          // Show invoice amount dialog
          const approvalData = await showInvoiceApprovalDialog(orderWithInvoice);

          if (approvalData) {
            const { invoiceAmount, invoiceCurrency } = approvalData;
            const approveResult = await apiService.approveOrderInvoice(editingOrder.id, invoiceAmount, invoiceCurrency);

            const updatedOrder = {
              ...orderWithInvoice,
              status: 'tamamlandi' as Order['status'],
              invoice_amount: invoiceAmount,
              invoice_currency: invoiceCurrency as Order['currency'],
              capex_refund_amount: approveResult?.capex_refund_amount
            };
            setEditingOrder(updatedOrder);
            setOrderData({...orderData, status: 'tamamlandi', invoice_url: result.invoice_url});
            setOrdersData(prevOrders =>
              prevOrders.map(o => o.id === editingOrder.id ? {...o, status: 'tamamlandi'} : o)
            );

            let successMsg = t('order.notifInvoiceApproved');
            if (approveResult?.capex_refund_amount && approveResult.capex_refund_amount > 0) {
              successMsg += ` CAPEX'e €${parseFloat(approveResult.capex_refund_amount).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })} ${t('order.notifInvoiceCapexRefunded')}`;
            }
            showGlobalNotification.success(t('order.notifSuccess'), successMsg);
            setShowModal(false);
            setEditingOrder(null);
          }
          // If user cancelled the amount dialog, stay in fatura-yuklendi state
        } else {
          showGlobalNotification.success(t('order.notifSuccess'), t('order.notifInvoiceUploaded'));
        }

        await Promise.all([loadData(), loadAllOrders()]);
      } catch (error: any) {
        showGlobalNotification.error(t('order.notifError'), error.message || t('order.notifInvoiceError'));
      }
    }
  };

  const convertToEUR = (amount: number, currency: string) => {
    if (currency === 'EUR') return amount;
    if (currency === 'USD') return amount * (exchangeRates.USD / exchangeRates.EUR);
    if (currency === 'TRY') return amount / exchangeRates.EUR;
    return amount;
  };

  const handleSave = async () => {
    // Fatura onaylanan siparişler güncellenemez
    if (editingOrder && editingOrder.status === 'fatura-onaylandi') {
      Swal.fire({
        title: t('order.swalUpdateBlockedTitle'),
        text: t('order.swalUpdateBlockedText'),
        icon: 'warning',
        confirmButtonText: t('order.swalOk'),
        confirmButtonColor: '#3b82f6'
      });
      return;
    }
    
    // Calculate total amount from items
    const calculatedTotal = (orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price * (1 + item.vat_rate / 100)), 0);
    const { use_capex, ...cleanOrderData } = orderData;
    const updatedOrderData = { 
      ...cleanOrderData, 
      total_amount: calculatedTotal,
      capex_id: orderData.capex_id > 0 ? orderData.capex_id : null
    };
    
    if (!orderData.order_number || !orderData.company_id || !orderData.location_id || !orderData.description) {
      showGlobalNotification.error(t('order.notifError'), t('order.notifRequiredFields'));
      return;
    }
    
    // CAPEX tutar kontrolü (KDV hariç) - Bakim kategorisi hariç
    if (updatedOrderData.use_capex && selectedCapex && orderData.category !== 'bakim') {
      // Calculate amount excluding VAT for CAPEX control
      const totalAmountExcludingVAT = (orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const orderAmountEUR = convertToEUR(totalAmountExcludingVAT, updatedOrderData.currency);
      const availableAmount = selectedCapex.available_amount || 0;

      if (availableAmount <= 0) {
        showGlobalNotification.error(
          t('order.notifCapexUnavailable'),
          t('order.notifCapexUnavailableMsg')
        );
        return;
      }

      if (orderAmountEUR > availableAmount) {
        showGlobalNotification.error(
          t('order.notifCapexInsufficient'),
          language === 'en'
            ? `Order amount excl. VAT (€${orderAmountEUR.toLocaleString('en-US', { minimumFractionDigits: 2 })}) exceeds the available amount of the selected CAPEX (€${availableAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}). Please select another CAPEX or reduce the order amount.`
            : `Sipariş tutarı KDV hariç (€${orderAmountEUR.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}) seçilen CAPEX'in kullanılabilir tutarını (€${availableAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}) aşıyor. Lütfen başka bir CAPEX seçin veya sipariş tutarını azaltın.`
        );
        return;
      }
    }
    
    const saveWithRetry = async (data: any, retryCount = 0): Promise<void> => {
      try {
        if (editingOrder) {
          await apiService.updateOrder(editingOrder.id, data);
          showGlobalNotification.success(t('order.notifSuccess'), t('order.notifUpdated'));
        } else {
          await apiService.createOrder(data);
          showGlobalNotification.success(t('order.notifSuccess'), t('order.notifCreated'));
        }
      } catch (error: any) {
        if ((error.message?.includes('sipariş numarası zaten kullanımda') || error.message?.includes('order_number')) && retryCount < 10 && !editingOrder) {
          // Otomatik olarak bir sonraki numarayı dene
          const parts = data.order_number.split('-');
          const currentNum = parseInt(parts[3]) || 1;
          const newNum = currentNum + 1;
          const newOrderNumber = `${parts[0]}-${parts[1]}-${parts[2]}-${String(newNum).padStart(3, '0')}`;
          
          setOrderData({...orderData, order_number: newOrderNumber});
          
          const newData = {...data, order_number: newOrderNumber};
          await saveWithRetry(newData, retryCount + 1);
        } else {
          throw error;
        }
      }
    };
    
    try {
      await saveWithRetry(updatedOrderData);

      // Reload data
      await Promise.all([loadData(), loadAllOrders()]);
      setShowModal(false);
      setOrderData({
        order_number: '',
        beam_order_number: '',
        ticket_number: '',
        location_id: 0,
        company_id: 0,
        capex_id: 0,
        user_id: user?.id || 0,
        supplier_id: 0,
        category: 'bilgisayar',
        total_amount: 0,
        currency: 'TRY',
        status: 'siparis-olusturuldu',
        description: '',
        notes: '',
        delivery_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        invoice_url: '',
        items: [{ item_name: '', quantity: 1, unit_price: 0, vat_rate: 20 }]
      });
    } catch (error: any) {
      // Check if this is a CAPEX requirement error
      if (error.capex_required || error.message?.includes('CAPEX seçimi zorunludur')) {
        const limit = error.limit || 200;
        const amount = error.amount || 0;
        const amountExcludingVAT = error.amount_excluding_vat;

        Swal.fire({
          title: t('order.swalCapexRequiredTitle'),
          html: `
            <div class="text-left space-y-3">
              <p class="text-gray-700 dark:text-gray-300">
                <strong>${t('order.swalCapexRequiredAmountLabel')}</strong> $${amount} USD
              </p>
              ${amountExcludingVAT ? `
                <p class="text-gray-500 dark:text-gray-400 text-sm">
                  (${orderData.currency} ${parseFloat(amountExcludingVAT).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })})
                </p>
              ` : ''}
              <p class="text-gray-700 dark:text-gray-300">
                <strong>${t('order.swalCapexRequiredLimitLabel')}</strong> $${limit} ${t('order.swalCapexRequiredLimitSuffix')}
              </p>
              <div class="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p class="text-yellow-800">
                  ⚠️ ${t('order.swalCapexRequiredMsg')} ${limit} ${t('order.swalCapexRequiredMsg2')}
                </p>
                <p class="text-yellow-800 mt-2">
                  ${t('order.swalCapexRequiredRetry')}
                </p>
              </div>
            </div>
          `,
          icon: 'warning',
          confirmButtonText: t('order.swalOk'),
          confirmButtonColor: '#f59e0b'
        });
        return;
      }

      showGlobalNotification.error(t('order.notifError'), error.message || t('order.notifSaveError'));
    }
  };

  const handleDelete = async (id: number) => {
    const orderToDelete = ordersData.find(o => o.id === id);
    if (!orderToDelete) return;
    
    const result = await Swal.fire({
      title: t('order.swalDeleteTitle'),
      html: `
        <div class="text-left">
          <p><strong>${t('order.swalDeleteOrderNo')}</strong> ${orderToDelete.order_number}</p>
          <p><strong>${t('order.swalDeleteCompany')}</strong> ${orderToDelete.company?.name || (language === 'en' ? 'Unknown' : 'Bilinmiyor')}</p>
          <p><strong>${t('order.swalDeleteAmount')}</strong> ${getCurrencySymbol(orderToDelete.currency)}${(orderToDelete.total_amount || 0).toLocaleString()}</p>
          ${orderToDelete.invoice_url ? `<p class="text-red-600"><strong>${t('order.swalDeleteInvoiceWarn')}</strong></p>` : ''}
          <br>
          <p class="text-red-600">${t('order.swalDeleteIrreversible')}</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: t('order.swalDeleteYes'),
      cancelButtonText: t('order.swalCancel'),
      reverseButtons: true,
      focusCancel: true
    });
    
    if (result.isConfirmed) {
      try {
        await apiService.deleteOrder(id);
        await Promise.all([loadData(), loadAllOrders()]);
        
        Swal.fire({
          title: t('order.swalDeletedTitle'),
          text: `${orderToDelete.order_number} ${t('order.swalDeletedText')}`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          timer: 3000,
          timerProgressBar: true
        });
        
        // Log activity
        await logActivity('delete_order', `${orderToDelete.order_number} siparişi silindi${orderToDelete.invoice_url ? ' (fatura dosyası dahil)' : ''}`, id.toString());
        
      } catch (error: any) {
        Swal.fire({
          title: t('order.swalErrorTitle'),
          text: error.message || t('order.notifSaveError'),
          icon: 'error',
          confirmButtonColor: '#dc2626'
        });
      }
    }
  };

  const showOrderDetail = async (order: Order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
    
    // Load order activities
    try {
      const activities = await apiService.getOrderActivities(order.id);
      setOrderActivities(activities || []);
    } catch (error) {
      setOrderActivities([]);
    }
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'beklemede':
        return Clock;
      case 'onaylandi':
        return CheckCircle;
      case 'siparis-verildi':
        return ShoppingCart;
      case 'teslim-edildi':
        return CheckCircle;
      case 'fatura-yuklendi':
        return Upload;
      case 'fatura-onaylandi':
        return CheckCircle;
      case 'tamamlandi':
        return CheckCircle;
      case 'iptal-edildi':
        return XCircle;
      default:
        return Clock;
    }
  };

  const getPriorityColor = (priority: string | undefined) => {
    return priorityOptions.find(p => p.value === priority)?.color || 'text-gray-600 dark:text-gray-400';
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  // Pagination functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Filtreleme yapıldıysa backend'den veri çekme, frontend pagination kullan
    if (!searchTerm && filterStatus === 'all') {
      loadData(page);
    }
  };

  const handleNextPage = () => {
    if (useFrontendPagination) {
      if (currentPageToUse < totalFilteredPages) {
        handlePageChange(currentPageToUse + 1);
      }
    } else {
      if (pagination.hasNextPage) {
        handlePageChange(currentPage + 1);
      }
    }
  };

  const handlePrevPage = () => {
    if (useFrontendPagination) {
      if (currentPageToUse > 1) {
        handlePageChange(currentPageToUse - 1);
      }
    } else {
      if (pagination.hasPrevPage) {
        handlePageChange(currentPage - 1);
      }
    }
  };

  const logActivity = async (action: string, description: string, relatedId?: string) => {
    try {
      await apiService.logActivity({
        action,
        description,
        related_id: relatedId
      });
    } catch (error) {
      // Silent fail for activity logging
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('order.pageTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('order.pageSubtitle')}</p>
        </div>
        <button
          onClick={handleAdd}
          disabled={!canCreate}
          className={`flex items-center justify-center px-4 py-3 rounded-lg transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto ${
            canCreate
              ? 'bg-primary text-white hover:bg-primary-dark'
              : 'bg-gray-300 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
          title={!canCreate ? t('order.noPermission') : ''}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('order.newOrder')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 mb-6">
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[80px] sm:min-h-[100px]"
          onClick={() => setFilterStatus('all')}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('order.statAll')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{allOrdersData.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
        </div>

        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[80px] sm:min-h-[100px]"
          onClick={() => setFilterStatus('siparis-olusturuldu')}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('order.statNew')}</p>
              <p className="text-lg sm:text-2xl font-bold text-primary">
                {allOrdersData.filter(o => o.status === 'siparis-olusturuldu').length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
        </div>

        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[80px] sm:min-h-[100px]"
          onClick={() => setFilterStatus('teslim-edildi')}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('order.statInvoicePending')}</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">
                {allOrdersData.filter(o => o.status === 'teslim-edildi').length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>




        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[80px] sm:min-h-[100px]"
          onClick={() => setFilterStatus('iptal-edildi')}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('order.statCancelled')}</p>
              <p className="text-lg sm:text-2xl font-bold text-red-600">
                {allOrdersData.filter(o => o.status === 'iptal-edildi').length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[80px] sm:min-h-[100px]"
          onClick={() => setFilterStatus('completed')}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('order.statCompleted')}</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">
                {allOrdersData.filter(o =>
                  o.status === 'fatura-yuklendi' ||
                  o.status === 'fatura-onaylandi' ||
                  o.status === 'tamamlandi'
                ).length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[80px] sm:min-h-[100px]"
          onClick={() => setFilterStatus('my-orders')}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('order.statMine')}</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600">
                {user ? allOrdersData.filter(o => o.user_id === user.id).length : 0}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <User className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* TCMB Exchange Rates */}
      {exchangeRates && (
        <div className="bg-gradient-to-r from-blue-500 to-teal-600 rounded-lg shadow-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <div className="bg-white/20 rounded-lg p-2 mr-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('order.exchangeTitle')}</h3>
                <p className="text-accent text-sm">
                  {exchangeRates.lastUpdated ?
                    `${t('order.exchangeLastUpdate')} ${new Date(exchangeRates.lastUpdated).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}`
                    : t('order.exchangeLoading')
                  }
                </p>
              </div>
            </div>
            <button
              onClick={updateExchangeRates}
              className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg text-sm transition-colors"
            >
              {t('order.exchangeRefresh')}
            </button>
          </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm font-medium">USD/TRY</span>
                  <span className="text-white text-lg font-bold">
                    ₺{exchangeRates.USD?.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                  </span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm font-medium">EUR/TRY</span>
                  <span className="text-white text-lg font-bold">
                    ₺{exchangeRates.EUR?.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                  </span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm font-medium">EUR/USD</span>
                  <span className="text-white text-lg font-bold">
                    ${(exchangeRates.EUR && exchangeRates.USD ? exchangeRates.EUR / exchangeRates.USD : 0)?.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                  </span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm font-medium">{t('order.exchangeLastUpdateLabel')}</span>
                  <span className="text-white text-sm font-bold">
                    {exchangeRates.lastUpdated ? new Date(exchangeRates.lastUpdated).toLocaleTimeString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}
                  </span>
                </div>
              </div>
            </div>
          {exchangeRates.error && (
            <div className="mt-3 bg-red-500/20 text-red-100 px-3 py-2 rounded-lg text-sm">
              ⚠️ {exchangeRates.error}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('order.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full"
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">{t('order.filterAll')}</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>

        <select
          value={filterYear}
          onChange={(e) => setFilterYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          {Array.from({ length: 5 }, (_, i) => {
            const year = new Date().getFullYear() - i;
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>

      </div>

      {/* Orders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colOrderNo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colCompany')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colOpenedBy')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colCategory')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colAmount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colDate')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('order.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {(paginatedFilteredOrders || []).map((order) => {
                const StatusIcon = getStatusIcon(order.status);
                return (
                  <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center mr-3">
                          <ShoppingCart className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {order.order_number}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {order.ticket_number}
                          </div>
                          {order.beam_order_number && (
                            <div className="text-xs text-primary">
                              ERP: {order.beam_order_number}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{order.company?.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{order.location?.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="text-sm text-gray-900 dark:text-gray-100">{order.user?.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{getCategoryLabel(order.category)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-primary">
                        {getCurrencySymbol(order.currency)}{((order.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) || (order.total_amount / 1.2)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <StatusIcon className="w-4 h-4 mr-2" />
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {statusOptions.find(s => s.value === order.status)?.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date(order.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => showOrderDetail(order)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('order.detailsTooltip')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <RoleBasedComponent requiredPermissions={['orders.update']} hideIfNoAccess>
                          <button
                            onClick={() => handleEdit(order)}
                            className="text-gray-400 hover:text-primary transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </RoleBasedComponent>
                        <RoleBasedComponent requiredPermissions={['orders.delete']} hideIfNoAccess>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </RoleBasedComponent>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {paginatedFilteredOrders.length === 0 && (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg">{t('order.emptyTitle')}</p>
              <p className="text-gray-400">{t('order.emptyText')}</p>
            </div>
          )}

          {/* Pagination */}
          {totalFilteredPages >= 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                <span>
                  {language === 'en'
                    ? `Page ${currentPageToUse} / ${totalFilteredPages} (Total ${useFrontendPagination ? filteredOrders.length : totalCount} orders)`
                    : `Sayfa ${currentPageToUse} / ${totalFilteredPages} (Toplam ${useFrontendPagination ? filteredOrders.length : totalCount} sipariş)`
                  }
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPageToUse - 1)}
                  disabled={currentPageToUse <= 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    currentPageToUse > 1
                      ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                  }`}
                >
                  {t('order.paginationPrev')}
                </button>

                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalFilteredPages) }, (_, i) => {
                    let pageNum;
                    if (totalFilteredPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPageToUse <= 3) {
                      pageNum = i + 1;
                    } else if (currentPageToUse >= totalFilteredPages - 2) {
                      pageNum = totalFilteredPages - 4 + i;
                    } else {
                      pageNum = currentPageToUse - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          pageNum === currentPageToUse
                            ? 'bg-primary text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPageToUse + 1)}
                  disabled={currentPageToUse >= totalFilteredPages}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    currentPageToUse < totalFilteredPages
                      ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                  }`}
                >
                  {t('order.paginationNext')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingOrder ? t('order.modalEditTitle') : t('order.modalNewTitle')}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Fatura onaylanmış sipariş uyarısı */}
              {editingOrder && editingOrder.status === 'fatura-onaylandi' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-red-800">{t('order.alertEditBlockedTitle')}</p>
                      <p className="text-xs text-red-600 mt-1">
                        {t('order.alertEditBlockedText')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formOrderNo')}
                  </label>
                  <input
                    type="text"
                    value={orderData.order_number}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                    placeholder="SP-2024-01-001"
                  />
                  {!editingOrder && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('order.formOrderNoAuto')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <span title={t('order.formBeamOrderNoTooltip')} className="cursor-help border-b border-dashed border-gray-400">{t('order.formBeamOrderNo')}</span>
                  </label>
                  <input
                    type="text"
                    value={orderData.beam_order_number}
                    onChange={(e) => setOrderData({...orderData, beam_order_number: e.target.value})}
                    readOnly={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                    placeholder="ERP-2024-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formTicketNo')}
                  </label>
                  <input
                    type="text"
                    value={orderData.ticket_number}
                    onChange={(e) => setOrderData({...orderData, ticket_number: e.target.value})}
                    readOnly={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                    placeholder="TK-000001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formCompany')}
                  </label>
                  <select
                    value={orderData.company_id}
                    onChange={(e) => {
                      const companyId = parseInt(e.target.value);
                      setOrderData({...orderData, company_id: companyId, capex_id: 0});
                      if (companyId) loadCompanyCapex(companyId);
                    }}
                    disabled={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  >
                    <option value={0}>{t('order.selectCompany')}</option>
                    {(companiesData || []).map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formLocation')}
                  </label>
                  <select
                    value={orderData.location_id}
                    onChange={(e) => setOrderData({...orderData, location_id: parseInt(e.target.value)})}
                    disabled={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  >
                    <option value={0}>{t('order.selectLocation')}</option>
                    {(locationsData || []).map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formCategory')}
                  </label>
                  <select
                    value={orderData.category}
                    onChange={(e) => setOrderData({...orderData, category: e.target.value as Order['category']})}
                    disabled={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  >
                    {(categories || []).map((category) => (
                      <option key={category} value={category}>
                        {getCategoryLabel(category)}
                      </option>
                    ))}
                  </select>
                </div>





                {editingOrder && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('order.formStatus')}
                    </label>
                    <select
                      value={orderData.status}
                      onChange={(e) => {
                        const newStatus = e.target.value as Order['status'];
                        // Tamamlandı durumu manuel seçilemez
                        if (newStatus === 'tamamlandi' && editingOrder?.status !== 'tamamlandi') {
                          showGlobalNotification.error(t('order.notifError'), t('order.notifStatusAutoSet'));
                          return;
                        }
                        // Fatura onaylandı durumu için kontrol
                        if (newStatus === 'fatura-onaylandi' && !editingOrder?.invoice_url) {
                          showGlobalNotification.error(t('order.notifError'), t('order.notifNeedInvoiceFirst'));
                          return;
                        }
                        setOrderData({...orderData, status: newStatus});
                      }}
                      disabled={editingOrder?.status === 'fatura-onaylandi'}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                    >
                      {(statusOptions || []).map((status) => {
                        // Fatura yüklendikten sonra geriye dönüş engelleme
                        const isInvoiceUploaded = editingOrder?.status === 'fatura-yuklendi' || editingOrder?.status === 'tamamlandi';
                        const isBackwardStatus = ['siparis-olusturuldu', 'onaylandi', 'teslim-edildi'].includes(status.value);
                        // Tamamlandı sadece fatura yükleme akışıyla otomatik ayarlanır, manuel seçilemez
                        const isTamamlandi = status.value === 'tamamlandi' && editingOrder?.status !== 'tamamlandi';
                        const isDisabled = (status.value === 'fatura-onaylandi' && !editingOrder?.invoice_url) ||
                                         (isInvoiceUploaded && isBackwardStatus) ||
                                         isTamamlandi;

                        return (
                          <option
                            key={status.value}
                            value={status.value}
                            disabled={isDisabled}
                          >
                            {status.label}
                            {status.value === 'fatura-onaylandi' && !editingOrder?.invoice_url ? t('order.statusNeedsInvoice') : ''}
                            {isInvoiceUploaded && isBackwardStatus ? t('order.statusNoReturn') : ''}
                            {isTamamlandi ? t('order.statusAutoSet') : ''}
                          </option>
                        );
                      })}
                    </select>
                    {orderData.status === 'fatura-onaylandi' && !editingOrder?.invoice_url && (
                      <p className="text-xs text-red-600 mt-1">
                        {t('order.statusNeedsInvoiceFirst')}
                      </p>
                    )}
                  </div>
                )}



                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formSupplier')}
                  </label>
                  <select
                    value={orderData.supplier_id}
                    onChange={(e) => setOrderData({...orderData, supplier_id: parseInt(e.target.value)})}
                    disabled={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  >
                    <option value={0}>{t('order.selectSupplier')}</option>
                    {(suppliersData || []).sort((a, b) => a.name.localeCompare(b.name, 'tr')).map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  {editingOrder ? (
                    // Düzenleme modunda - CAPEX değiştirilemez
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('order.formCapex')}
                      </label>
                      {orderData.use_capex && orderData.capex_id > 0 ? (
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
                          <div className="text-sm text-gray-800 dark:text-gray-200">
                            <strong>{t('order.capexUsed')}</strong> {editingOrder.capex?.capex_number || '...'}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            <strong>{t('order.capexCategory')}</strong> {editingOrder.capex?.category || '-'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            {t('order.capexImmutableWarning')}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {t('order.capexNone')}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Yeni sipariş modunda - CAPEX seçilebilir
                    <div>
                      <label className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={orderData.use_capex}
                          onChange={(e) => {
                            const useCapex = e.target.checked;
                            setOrderData({...orderData, use_capex: useCapex, capex_id: 0});
                            if (useCapex && orderData.company_id) {
                              loadCompanyCapex(orderData.company_id);
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('order.useCapex')}</span>
                      </label>
                      
                      {orderData.use_capex && (
                        <div>
                          <select
                            value={orderData.capex_id}
                            onChange={(e) => {
                              const capexId = parseInt(e.target.value);
                              const capex = companyCapexData.find(c => c.id === capexId);
                              setOrderData({...orderData, capex_id: capexId});
                              setSelectedCapex(capex || null);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value={0}>{t('order.selectCapex')}</option>
                            {(companyCapexData || []).map((capex) => (
                              <option key={capex.id} value={capex.id}>
                                {capex.capex_number} - {capex.category}
                              </option>
                            ))}
                          </select>
                          {selectedCapex && (
                            <div className="mt-2 p-3 bg-primary/10 border border-primary/30 rounded-md">
                              <div className="text-sm text-primary-dark">
                                <strong>{t('order.capexSelected')}</strong> {selectedCapex.capex_number}
                              </div>
                              <div className="text-sm text-primary-dark mt-1">
                                <strong>{t('order.capexAvailable')}</strong> €{selectedCapex.available_amount?.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <div className="text-xs text-primary mt-1">
                                {t('order.capexTotal')} €{selectedCapex.total_amount?.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })} |
                                {t('order.capexUsedAmount')} €{selectedCapex.used_amount?.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })} |
                                {t('order.capexBlocked')} €{selectedCapex.blocked_amount?.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('order.formDeliveryDate')}
                  </label>
                  <input
                    type="date"
                    value={orderData.delivery_date}
                    onChange={(e) => setOrderData({...orderData, delivery_date: e.target.value})}
                    readOnly={editingOrder?.status === 'fatura-onaylandi'}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('order.formDescription')}
                </label>
                <textarea
                  value={orderData.description}
                  onChange={(e) => setOrderData({...orderData, description: e.target.value})}
                  readOnly={editingOrder?.status === 'fatura-onaylandi'}
                  rows={3}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  placeholder={t('order.formDescPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('order.formNotes')}
                </label>
                <textarea
                  value={orderData.notes}
                  onChange={(e) => setOrderData({...orderData, notes: e.target.value})}
                  readOnly={editingOrder?.status === 'fatura-onaylandi'}
                  rows={2}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md ${editingOrder?.status === 'fatura-onaylandi' ? 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'}`}
                  placeholder={t('order.formNotesPlaceholder')}
                />
              </div>

              {/* Sipariş Kalemleri */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('order.itemsTitle')}</h3>
                  {!editingOrder && (
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('order.currency')}</label>
                        <select
                          value={orderData.currency}
                          onChange={(e) => setOrderData({...orderData, currency: e.target.value as 'TRY' | 'USD' | 'EUR'})}
                          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="TRY">₺</option>
                          <option value="USD">$</option>
                          <option value="EUR">€</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOrderData({...orderData, items: [...(orderData.items || []), { item_name: '', quantity: 1, unit_price: 0, vat_rate: 20 }]})}
                        className="flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('order.addItem')}
                      </button>
                    </div>
                  )}
                  {editingOrder && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('order.currency')} {orderData.currency === 'TRY' ? '₺' : orderData.currency === 'USD' ? '$' : '€'}
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  {(orderData.items || []).map((item, index) => (
                    <div key={index} className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${editingOrder ? 'bg-gray-50 dark:bg-gray-700' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('order.itemN')} {index + 1}</h4>
                        {!editingOrder && (orderData.items || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newItems = (orderData.items || []).filter((_, i) => i !== index);
                              setOrderData({...orderData, items: newItems});
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {editingOrder && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('order.immutable')}</span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {t('order.itemName')}
                          </label>
                          <input
                            type="text"
                            value={item.item_name}
                            onChange={(e) => handleItemNameChange(index, e.target.value)}
                            onBlur={() => {
                              // Delay hiding to allow click on suggestion
                              setTimeout(() => {
                                setShowSuggestions({...showSuggestions, [index]: false});
                              }, 200);
                            }}
                            onFocus={() => {
                              if (item.item_name && item.item_name.length >= 4 && itemSuggestions.length > 0) {
                                setShowSuggestions({...showSuggestions, [index]: true});
                              }
                            }}
                            readOnly={!!editingOrder}
                            className={`w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm ${editingOrder ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'focus:outline-none focus:ring-1 focus:ring-primary'}`}
                            placeholder="Dell Optiplex 7090 (min 4 karakter)"
                            autoComplete="off"
                          />

                          {/* Autocomplete Suggestions Dropdown */}
                          {!editingOrder && showSuggestions[index] && itemSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {itemSuggestions.map((suggestion, suggIdx) => (
                                <div
                                  key={suggIdx}
                                  onClick={() => selectSuggestion(index, suggestion)}
                                  className="px-3 py-2 hover:bg-primary/10 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {suggestion.item_name}
                                      </div>
                                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        {suggestion.currency} {suggestion.unit_price.toFixed(2)} • KDV: %{suggestion.vat_rate}
                                        {suggestion.last_used && (
                                          <span className="ml-2 text-primary">
                                            • {(() => {
                                              const d = new Date(suggestion.last_used);
                                              const now = new Date();
                                              const diffMs = now.getTime() - d.getTime();
                                              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                                              if (diffDays === 0) return t('order.today');
                                              if (diffDays === 1) return t('order.yesterday');
                                              if (diffDays < 7) return `${diffDays} ${t('order.daysAgo')}`;
                                              return d.toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
                                            })()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs text-primary ml-2">
                                      {t('order.clickToAdd')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Loading indicator */}
                          {suggestionLoading && showSuggestions[index] && (
                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg p-3 text-center">
                              <div className="text-sm text-gray-600 dark:text-gray-400">{t('order.searching')}</div>
                            </div>
                          )}

                          {/* Price Search Button */}
                          {!editingOrder && item.item_name && item.item_name.length >= 3 && (
                            <button
                              type="button"
                              onClick={() => searchPrice(index, item.item_name)}
                              disabled={priceSearchLoading[index]}
                              className="absolute right-2 top-7 text-orange-500 hover:text-orange-700 disabled:text-gray-400"
                              title={t('order.priceSearch')}
                            >
                              {priceSearchLoading[index] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <TrendingDown className="w-4 h-4" />
                              )}
                            </button>
                          )}

                          {/* Price Search Results Popup */}
                          {showPricePopup[index] && priceSearchResults[index] && (
                            <>
                              {/* Backdrop */}
                              <div
                                className="fixed inset-0 bg-black bg-opacity-30 z-[9998]"
                                onClick={() => setShowPricePopup({...showPricePopup, [index]: false})}
                              />
                              {/* Popup */}
                              <div className="fixed z-[9999] w-80 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-lg flex justify-between items-center">
                                  <span className="font-medium text-sm">{t('order.priceCompare')}</span>
                                  <button
                                    onClick={() => setShowPricePopup({...showPricePopup, [index]: false})}
                                    className="text-white hover:text-orange-200"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>

                              <div className="p-3 max-h-64 overflow-y-auto">
                                {priceSearchResults[index].results && priceSearchResults[index].results.length > 0 ? (
                                  <>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                      {priceSearchResults[index].results.length} {t('order.priceResultsN')}
                                    </div>
                                    {priceSearchResults[index].results.map((result: any, rIdx: number) => (
                                      <a
                                        key={rIdx}
                                        href={result.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block p-2 hover:bg-orange-50 rounded border-b border-gray-100 last:border-0"
                                      >
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1 pr-2">
                                            <div className="text-xs text-gray-800 dark:text-gray-200 line-clamp-2">{result.name}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{result.source}</div>
                                          </div>
                                          <div className="text-sm font-bold text-green-600 whitespace-nowrap">
                                            {result.price.toLocaleString('tr-TR')} ₺
                                          </div>
                                        </div>
                                      </a>
                                    ))}
                                  </>
                                ) : (
                                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                    {t('order.priceNoResults')}
                                  </div>
                                )}
                              </div>

                              {/* Search Links */}
                              {priceSearchResults[index].searchLinks && (
                                <div className="p-3 bg-gray-50 dark:bg-gray-700 border-t rounded-b-lg">
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t('order.priceSearchOn')}</div>
                                  <div className="flex flex-wrap gap-1">
                                    {Object.entries(priceSearchResults[index].searchLinks).map(([site, url]) => (
                                      <a
                                        key={site}
                                        href={url as string}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-xs text-primary hover:bg-primary/10 hover:border-primary/50"
                                      >
                                        {site.charAt(0).toUpperCase() + site.slice(1)}
                                        <ExternalLink className="w-3 h-3 ml-1" />
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                              </div>
                            </>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {t('order.itemQty')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const newItems = [...(orderData.items || [])];
                              newItems[index] = {...newItems[index], quantity: parseInt(e.target.value) || 1};
                              setOrderData({...orderData, items: newItems});
                            }}
                            readOnly={!!editingOrder}
                            className={`w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm ${editingOrder ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'focus:outline-none focus:ring-1 focus:ring-primary'}`}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {t('order.itemUnitPrice')}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_price}
                            onChange={(e) => {
                              const newItems = [...(orderData.items || [])];
                              newItems[index] = {...newItems[index], unit_price: parseFloat(e.target.value) || 0};
                              setOrderData({...orderData, items: newItems});
                            }}
                            readOnly={!!editingOrder}
                            className={`w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm ${editingOrder ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'focus:outline-none focus:ring-1 focus:ring-primary'}`}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {t('order.itemVat')}
                          </label>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            value={item.vat_rate}
                            onChange={(e) => {
                              const newItems = [...(orderData.items || [])];
                              newItems[index] = {...newItems[index], vat_rate: parseInt(e.target.value) || 0};
                              setOrderData({...orderData, items: newItems});
                            }}
                            readOnly={!!editingOrder}
                            className={`w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm ${editingOrder ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'focus:outline-none focus:ring-1 focus:ring-primary'}`}
                          />
                        </div>
                      </div>
                      
                      <div className="mt-2 text-right">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {orderData.currency === 'TRY' ? '₺' : orderData.currency === 'USD' ? '$' : '€'}
                          {(item.quantity * item.unit_price * (1 + item.vat_rate / 100)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t('order.exclVat')} {(item.quantity * item.unit_price).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })} {t('order.plusVat')}({item.vat_rate}%)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-right space-y-1">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t('order.totalWithVat')} {orderData.currency === 'TRY' ? '₺' : orderData.currency === 'USD' ? '$' : '€'}
                      {(orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price * (1 + item.vat_rate / 100)), 0).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-primary bg-primary/10 p-2 rounded border border-primary/30 mt-2">
                      ℹ️ <strong>{language === 'en' ? 'Info' : 'Bilgi'}:</strong> {t('order.vatInfo')}
                    </div>
                    
                    {/* Güncel Kur Bilgileri */}
                    <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-700 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{t('order.exchangeRatesLabel')}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {exchangeRates.lastUpdated ? new Date(exchangeRates.lastUpdated).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR') : t('order.notCurrentRate')}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-1 text-xs">
                        <div className="text-center">
                          <div className="font-medium">USD/TRY</div>
                          <div className="text-green-600">{exchangeRates.USD?.toFixed(4) || 'N/A'}</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium">EUR/TRY</div>
                          <div className="text-primary">{exchangeRates.EUR?.toFixed(4) || 'N/A'}</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium">USD/EUR</div>
                          <div className="text-purple-600">
                            {exchangeRates.USD && exchangeRates.EUR ? (exchangeRates.USD / exchangeRates.EUR).toFixed(4) : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {!editingOrder && orderData.use_capex && selectedCapex && (
                      <div className="text-sm mt-2 p-2 bg-primary/10 border border-primary/30 rounded">
                        <div className="text-primary-dark">
                          {t('order.capexCheck')} €{convertToEUR((orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0), orderData.currency).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })} / €{(selectedCapex.available_amount || 0).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}
                        </div>
                        {convertToEUR((orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0), orderData.currency) > (selectedCapex.available_amount || 0) && (
                          <div className="text-red-600 font-medium">
                            {t('order.capexInsufficient')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Invoice Upload Section - Only show if editing and status allows invoice operations */}
              {editingOrder && (orderData.status === 'teslim-edildi' || orderData.status === 'fatura-yuklendi' || orderData.status === 'fatura-onaylandi') && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.invoiceTitle')}</h3>
                  <div className="space-y-4">
                    {editingOrder.invoice_url ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <FileText className="w-5 h-5 text-green-600 mr-3" />
                              <div>
                                <p className="text-sm font-medium text-green-800">{t('order.invoiceUploaded')}</p>
                                <p className="text-xs text-green-600">
                                  {new Date(editingOrder.updated_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => window.open(editingOrder.invoice_url, '_blank')}
                                className="flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                {t('order.invoiceView')}
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm(t('order.confirmDeleteInvoice'))) {
                                    try {
                                      await apiService.deleteOrderInvoice(editingOrder.id);
                                      
                                      // Update both editing order and order data states
                                      const updatedOrder = {...editingOrder, invoice_url: '', status: 'teslim-edildi' as Order['status']};
                                      setEditingOrder(updatedOrder);
                                      setOrderData({...orderData, status: 'teslim-edildi', invoice_url: ''});
                                      
                                      // Update orders data array to reflect changes immediately
                                      setOrdersData(prevOrders => 
                                        prevOrders.map(order => 
                                          order.id === editingOrder.id 
                                            ? {...order, invoice_url: '', status: 'teslim-edildi'}
                                            : order
                                        )
                                      );
                                      
                                      showGlobalNotification.success(t('order.notifSuccess'), t('order.notifInvoiceDeleted'));

                                      // Log activity
                                      await logActivity('delete_invoice', `${editingOrder.order_number} siparişinin faturası silindi`, editingOrder.id.toString());

                                      await Promise.all([loadData(), loadAllOrders()]);
                                    } catch (error: any) {
                                      showGlobalNotification.error(t('order.notifError'), error.message || t('order.notifInvoiceDeleteError'));
                                    }
                                  }
                                }}
                                className="flex items-center px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                {t('order.invoiceDelete')}
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-green-700 bg-green-100 p-2 rounded">
                            <strong>{t('order.invoiceUploadedBy')}</strong> {editingOrder.invoice_uploaded_by_name || editingOrder.user?.name || (language === 'en' ? 'Unknown' : 'Bilinmiyor')}
                            <span className="ml-2">({new Date(editingOrder.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')})</span>
                          </div>
                        </div>
                        {orderData.status === 'fatura-yuklendi' && (
                          <button
                            onClick={async () => {
                              try {
                                const approvalData = await showInvoiceApprovalDialog(editingOrder);
                                if (!approvalData) return;

                                const { invoiceAmount, invoiceCurrency } = approvalData;
                                const approveResult = await apiService.approveOrderInvoice(editingOrder.id, invoiceAmount, invoiceCurrency);

                                const updatedOrder = {
                                  ...editingOrder,
                                  status: 'tamamlandi' as Order['status'],
                                  invoice_amount: invoiceAmount,
                                  invoice_currency: invoiceCurrency as Order['currency'],
                                  capex_refund_amount: approveResult?.capex_refund_amount
                                };
                                setEditingOrder(updatedOrder);
                                setOrderData({...orderData, status: 'tamamlandi'});
                                setOrdersData(prevOrders =>
                                  prevOrders.map(o => o.id === editingOrder.id ? {...o, status: 'tamamlandi'} : o)
                                );

                                let successMsg = t('order.notifInvoiceApproved');
                                if (approveResult?.capex_refund_amount && approveResult.capex_refund_amount > 0) {
                                  successMsg += ` CAPEX'e €${parseFloat(approveResult.capex_refund_amount).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })} ${t('order.notifInvoiceCapexRefunded')}`;
                                }
                                showGlobalNotification.success(t('order.notifSuccess'), successMsg);
                                await Promise.all([loadData(), loadAllOrders()]);
                              } catch (error: any) {
                                showGlobalNotification.error(t('order.notifError'), error.message || t('order.notifInvoiceApproveError'));
                              }
                            }}
                            className="w-full flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {t('order.invoiceApprove')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800">
                            {t('order.invoiceNeedDelivered')}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center px-4 py-2 bg-primary/20 text-primary-dark rounded-lg cursor-pointer hover:bg-primary/30 transition-colors">
                            <Upload className="w-4 h-4 mr-2" />
                            {t('order.invoiceSelectBtn')}
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={handleInvoiceUpload}
                              className="hidden"
                            />
                          </label>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {t('order.invoiceFileTypes')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show invoice approval info for approved/completed orders */}
              {editingOrder && (orderData.status === 'tamamlandi' || orderData.status === 'fatura-onaylandi') && editingOrder.invoice_url && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.invoiceApprovalTitle')}</h3>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                    <div className="flex items-center mb-1">
                      <FileText className="w-5 h-5 text-green-600 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          {editingOrder.invoice_approved_by_name ? t('order.invoiceApprovedLabel') : t('order.invoiceAutoCompletedLabel')}
                        </p>
                        <p className="text-xs text-green-600">
                          {t('order.invoiceOrderStatus')} {statusOptions.find(s => s.value === orderData.status)?.label}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-green-700 bg-green-100 p-2 rounded">
                      {editingOrder.invoice_approved_by_name ? (
                        <>
                          ✅ <strong>{language === 'en' ? 'Invoice approved' : 'Fatura onaylandı'}</strong>{' '}
                          {language === 'en'
                            ? `by ${editingOrder.invoice_approved_by_name} on ${new Date(editingOrder.invoice_approved_at).toLocaleString('en-US')}`
                            : `${editingOrder.invoice_approved_by_name} tarafından ${new Date(editingOrder.invoice_approved_at).toLocaleString('tr-TR')} tarihinde`
                          }
                        </>
                      ) : (
                        <>
                          🔄 <strong>{language === 'en' ? 'Order completed' : 'Sipariş tamamlandı'}</strong>{' '}
                          {language === 'en' ? 'automatically by the system' : 'sistem tarafından otomatik olarak'}
                        </>
                      )}
                    </div>
                    {editingOrder.invoice_amount != null && (
                      <div className="text-xs bg-blue-50 border border-blue-200 p-2 rounded space-y-1">
                        <div className="flex justify-between">
                          <span className="text-blue-700">{t('order.invoiceOrderAmount')}</span>
                          <span className="font-medium text-blue-900">{editingOrder.currency} {parseFloat(String(editingOrder.total_amount)).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">{t('order.invoiceInvoiceAmount')}</span>
                          <span className="font-medium text-blue-900">{editingOrder.invoice_currency || editingOrder.currency} {parseFloat(String(editingOrder.invoice_amount)).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {editingOrder.capex_refund_amount != null && parseFloat(String(editingOrder.capex_refund_amount)) > 0 && (
                          <div className="flex justify-between border-t border-blue-200 pt-1 mt-1">
                            <span className="text-purple-700 font-medium">{t('order.invoiceCapexRefund')}</span>
                            <span className="font-medium text-purple-900">€{parseFloat(String(editingOrder.capex_refund_amount)).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {editingOrder?.status === 'fatura-onaylandi' ? t('order.btnClose') : t('order.btnCancel')}
              </button>
              <button
                onClick={editingOrder?.status === 'fatura-onaylandi' ? () => {
                  Swal.fire({
                    title: t('order.swalUpdateBlockedTitle'),
                    text: t('order.swalUpdateBlockedText'),
                    icon: 'warning',
                    confirmButtonText: t('order.swalOk'),
                    confirmButtonColor: '#3b82f6'
                  });
                } : handleSave}
                disabled={editingOrder?.status === 'fatura-onaylandi'}
                className={`px-4 py-2 rounded-md transition-colors ${
                  editingOrder?.status === 'fatura-onaylandi'
                    ? 'bg-gray-400 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {editingOrder ? t('order.btnUpdate') : t('order.btnSave')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Order Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedOrder && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('order.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedOrder.order_number} - {selectedOrder.ticket_number}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.detailOrderInfo')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailOrderNo')}</span>
                      <span className="font-medium">{selectedOrder.order_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailTicketNo')}</span>
                      <span className="font-medium">{selectedOrder.ticket_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailCategory')}</span>
                      <span className="font-medium">{getCategoryLabel(selectedOrder.category)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailTotal')}</span>
                      <span className="font-medium text-primary">
                        {getCurrencySymbol(selectedOrder.currency)}{(selectedOrder.total_amount || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailStatus')}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedOrder.status)}`}>
                        {statusOptions.find(s => s.value === selectedOrder.status)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailPriority')}</span>
                      <span className={`font-medium ${getPriorityColor(selectedOrder.priority)}`}>
                        {priorityOptions.find(p => p.value === selectedOrder.priority)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.detailContactInfo')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailCompany')}</span>
                      <span className="font-medium">{selectedOrder.company?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailLocation')}</span>
                      <span className="font-medium">{selectedOrder.location?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('order.detailOrderer')}</span>
                      <span className="font-medium">{selectedOrder.user?.name}</span>
                    </div>
                    {selectedOrder.supplier && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('order.detailSupplier')}</span>
                        <span className="font-medium">{selectedOrder.supplier.name}</span>
                      </div>
                    )}
                    {selectedOrder.capex && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('order.detailCapex')}</span>
                        <span className="font-medium">{selectedOrder.capex.capex_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.detailDescription')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  {selectedOrder.description}
                </p>
              </div>

              {selectedOrder.notes && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.detailNotes')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedOrder.notes}
                  </p>
                </div>
              )}

              {selectedOrder.invoice_url && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.detailInvoice')}</h3>
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-green-600 mr-3" />
                      <span className="text-sm font-medium text-green-800">{t('order.detailInvoiceAvailable')}</span>
                    </div>
                    <button
                      onClick={() => window.open(selectedOrder.invoice_url, '_blank')}
                      className="flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      {t('order.detailView')}
                    </button>
                  </div>
                </div>
              )}

              {/* Activity History */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('order.activityTitle')}</h3>
                <div className="max-h-64 overflow-y-auto space-y-3">
                  {orderActivities.length > 0 ? (
                    orderActivities.map((activity, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            activity.action === 'create_order' ? 'bg-green-500' :
                            activity.action === 'update_order' ? 'bg-primary' :
                            activity.action === 'delete_order' ? 'bg-red-500' :
                            activity.action === 'upload_invoice' ? 'bg-cyan-500' :
                            activity.action === 'delete_invoice' ? 'bg-orange-500' :
                            activity.action === 'approve_invoice' ? 'bg-emerald-500' : 'bg-gray-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {activity.description}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {activity.user_name || t('order.activitySystem')}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(activity.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">{t('order.activityEmpty')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>{t('order.detailCreatedAt')}</span>
                  <div className="font-medium">{new Date(selectedOrder.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>{t('order.detailUpdatedAt')}</span>
                  <div className="font-medium">{new Date(selectedOrder.updated_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</div>
                </div>
              </div>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default OrdersPage;
